import type { Manifest } from "../manifest/schema.js";
import { formatGbp } from "../shop/basket.js";
import { clear, h } from "../ui/dom.js";
import type { ListKind, ProductLists } from "./lists.js";

export interface FeaturePadDeps {
  manifest: Manifest;
  lists: ProductLists;
  mediaUrl: (rel: string) => string;
  /** Floors from the lift's floor panel (archive already applied). */
  floors: { badge?: string; label: string; to: string; current?: boolean }[];
  openProduct: (id: string) => void;
  goTo: (locationId: string) => void;
  announce: (msg: string) => void;
}

export type PadTab = "membership" | "try-on" | "saved" | "wishlist" | "floors";

const TABS: { id: PadTab; label: string }[] = [
  { id: "membership", label: "Membership" },
  { id: "try-on", label: "Try-on lounges" },
  { id: "saved", label: "Saved items" },
  { id: "wishlist", label: "Wish list" },
  { id: "floors", label: "Floor directory" }
];

/** The lift's member "feature pad": a separate screen opened from the directory panel. Nothing leaves the device. */
export function featurePadContent(d: FeaturePadDeps, start: PadTab = "membership"): HTMLElement {
  let tab: PadTab = start;
  const body = h("div", { class: "pad-body", "data-testid": "pad-body" });
  const status = h("p", { class: "status", role: "status" });
  const tabButtons = TABS.map((t) =>
    h(
      "button",
      {
        type: "button",
        class: "pad-tab",
        role: "tab",
        "data-tab": t.id,
        onclick: () => {
          tab = t.id;
          render();
        }
      },
      t.label
    )
  );

  const productList = (kind: ListKind): HTMLElement => {
    const items = d.lists.ids(kind).map((id) => d.manifest.products.find((p) => p.id === id)).filter((p) => p !== undefined);
    if (items.length === 0)
      return h("p", { class: "muted", "data-testid": `pad-${kind}-empty` }, kind === "wishlist" ? "Your wish list is empty. Tap the heart on any priced item to add it." : "Nothing saved yet. Tap Save on any product to keep it here.");
    return h(
      "ul",
      { class: "pad-list", "data-testid": `pad-${kind}` },
      ...items.map((p) =>
        h(
          "li",
          { class: "pad-item" },
          h("img", { src: `${d.mediaUrl(p.image)}.jpg`, alt: "", width: 64, height: 80, loading: "lazy" }),
          h("div", {}, h("strong", {}, p.name), h("span", { class: "muted small" }, ` ${p.brand}`), h("p", { class: "price small" }, p.pricePence === undefined ? "Price on request" : formatGbp(p.pricePence))),
          h("button", { type: "button", class: "btn btn-small", onclick: () => d.openProduct(p.id) }, "View"),
          h(
            "button",
            {
              type: "button",
              class: "btn btn-small",
              "aria-label": `Remove ${p.name}`,
              onclick: () => {
                d.lists.toggle(kind, p.id);
                d.announce(`${p.name} removed`);
                render();
              }
            },
            "Remove"
          )
        )
      )
    );
  };

  function render(): void {
    for (const b of tabButtons) b.setAttribute("aria-selected", String(b.dataset.tab === tab));
    clear(body);
    status.textContent = "";
    body.dataset.tab = tab;
    switch (tab) {
      case "membership":
        body.append(
          h("h3", {}, "Hallmark Circle ", h("span", { class: "pad-badge" }, "Concept")),
          h("p", {}, "A members' circle for Hallmark Court: early access to new floors, private appointments, member-only clues in the weekly hunt and the Atelier for partner retailers."),
          h("ul", { class: "pad-tiers" }, h("li", {}, h("strong", {}, "Circle"), " - saved items and wish list across devices, weekly clue"), h("li", {}, h("strong", {}, "Gold"), " - private try-on lounge bookings, early access"), h("li", {}, h("strong", {}, "Black"), " - personal shopper, invitation-only evenings")),
          h("button", { type: "button", class: "btn btn-primary", onclick: () => (status.textContent = "Thank you - noted for the demo. Nothing is sent or stored.") }, "Register interest (demo)")
        );
        break;
      case "try-on":
        body.append(h("h3", {}, "Try-on lounges ", h("span", { class: "pad-badge" }, "Coming soon")), h("p", {}, "Private lounges where a host brings pieces from any floor for you to try on, in person or over video. Booking opens with membership."));
        break;
      case "saved":
        body.append(h("h3", {}, "Saved items"), productList("saved"));
        break;
      case "wishlist":
        body.append(h("h3", {}, "Wish list"), productList("wishlist"));
        break;
      case "floors":
        body.append(
          h("h3", {}, "Floor directory"),
          h(
            "ul",
            { class: "pad-floors" },
            ...d.floors.map((f) =>
              h(
                "li",
                {},
                h("button", { type: "button", class: `btn pad-floor${f.current ? " is-current" : ""}`, "data-to": f.to, disabled: f.current, onclick: () => d.goTo(f.to) }, h("span", { class: "menu-badge" }, f.badge ?? ""), ` ${f.label}`)
              )
            )
          )
        );
        break;
    }
  }

  const root = h(
    "div",
    { class: "pad", "data-testid": "feature-pad" },
    h("h2", { "data-autofocus": true, tabindex: -1 }, "Member feature pad"),
    h("div", { class: "pad-tabs", role: "tablist", "aria-label": "Feature pad" }, ...tabButtons),
    body,
    status,
    h("p", { class: "muted small" }, "Demo: lists are kept on this device only.")
  );
  render();
  return root;
}
